// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useEffect, useRef, useCallback, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { AppIcon } from './AppIcon';
import { IconButton } from './IconButton';
import { Button } from './Button';
import './Modal.css';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type ModalVariant = 'general' | 'warning';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  size?: 'small' | 'medium' | 'large' | 'xlarge' | 'fullscreen';
  variant?: ModalVariant;
  className?: string;
  children: React.ReactNode;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
}

export interface ModalHeaderProps {
  icon?: string;
  title: React.ReactNode;
  onClose?: () => void;
  className?: string;
}

export interface ModalContentProps {
  className?: string;
  children: React.ReactNode;
}

export interface ModalActionConfig {
  label: string;
  onClick: () => void;
  colour?: 'purple' | 'red' | 'green' | 'blue';
  loading?: boolean;
  disabled?: boolean;
  iconLeft?: string;
  iconRight?: string;
}

export interface ModalActionsProps {
  primaryAction?: ModalActionConfig;
  secondaryAction?: ModalActionConfig;
  className?: string;
  children?: React.ReactNode;
}

/* ------------------------------------------------------------------ */
/*  Context                                                            */
/* ------------------------------------------------------------------ */

const ModalContext = createContext<{ variant: ModalVariant }>({ variant: 'general' });

/* ------------------------------------------------------------------ */
/*  Compound sub-components                                            */
/* ------------------------------------------------------------------ */

function ModalHeader({ icon, title, onClose, className = '' }: ModalHeaderProps) {
  return (
    <div className={`fin-modal__header ${className}`.trim()}>
      <div className="fin-modal__header-left">
        {icon && (
          <span className="fin-modal__header-icon">
            <AppIcon name={icon} size={24} />
          </span>
        )}
        <div className="fin-modal__title">{title}</div>
      </div>
      {onClose && (
        <IconButton
          variant="ghost"
          size="xs"
          icon={<AppIcon name="close" />}
          onClick={onClose}
          aria-label="Close"
        />
      )}
    </div>
  );
}

function ModalContent({ className = '', children }: ModalContentProps) {
  const classes = ['fin-modal__content', className].filter(Boolean).join(' ');

  return <div className={classes}>{children}</div>;
}

function ModalActions({
  primaryAction,
  secondaryAction,
  className = '',
  children,
}: ModalActionsProps) {
  const { variant } = useContext(ModalContext);
  const defaultColour = variant === 'warning' ? 'red' : 'purple';

  // Allow arbitrary children for custom footer layouts
  if (children) {
    return <div className={`fin-modal__actions ${className}`.trim()}>{children}</div>;
  }

  return (
    <div className={`fin-modal__actions ${className}`.trim()}>
      {secondaryAction && (
        <Button
          variant="outlined"
          colour={secondaryAction.colour ?? defaultColour}
          onClick={secondaryAction.onClick}
          loading={secondaryAction.loading}
          disabled={secondaryAction.disabled}
          iconLeft={
            secondaryAction.iconLeft ? (
              <AppIcon name={secondaryAction.iconLeft} size={16} />
            ) : undefined
          }
          iconRight={
            secondaryAction.iconRight ? (
              <AppIcon name={secondaryAction.iconRight} size={16} />
            ) : undefined
          }
        >
          {secondaryAction.label}
        </Button>
      )}
      {primaryAction && (
        <Button
          variant="filled"
          colour={primaryAction.colour ?? defaultColour}
          onClick={primaryAction.onClick}
          loading={primaryAction.loading}
          disabled={primaryAction.disabled}
          iconLeft={
            primaryAction.iconLeft ? (
              <AppIcon name={primaryAction.iconLeft} size={16} />
            ) : undefined
          }
          iconRight={
            primaryAction.iconRight ? (
              <AppIcon name={primaryAction.iconRight} size={16} />
            ) : undefined
          }
        >
          {primaryAction.label}
        </Button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Root Modal                                                         */
/* ------------------------------------------------------------------ */

export function Modal({
  open,
  onClose,
  size = 'small',
  variant = 'general',
  className = '',
  children,
  closeOnOverlayClick = true,
  closeOnEscape = true,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<Element | null>(null);

  // Body scroll lock
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  // Escape key
  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, closeOnEscape, onClose]);

  // Focus management
  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement;
    // Delay focus so the element is mounted
    requestAnimationFrame(() => {
      modalRef.current?.focus();
    });
    return () => {
      if (previousFocusRef.current instanceof HTMLElement) {
        previousFocusRef.current.focus();
      }
    };
  }, [open]);

  // Focus trap
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Tab' || !modalRef.current) return;

      const focusable = modalRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [],
  );

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (closeOnOverlayClick && e.target === e.currentTarget) {
        onClose();
      }
    },
    [closeOnOverlayClick, onClose],
  );

  if (!open) return null;

  const modalClasses = [
    'fin-modal',
    `fin-modal--${size}`,
    variant === 'warning' && 'fin-modal--warning',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return createPortal(
    <ModalContext.Provider value={{ variant }}>
      <div
        className="fin-modal-overlay"
        onClick={handleOverlayClick}
        role="presentation"
      >
        <div
          ref={modalRef}
          className={modalClasses}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          onKeyDown={handleKeyDown}
        >
          {children}
        </div>
      </div>
    </ModalContext.Provider>,
    document.body,
  );
}

/* ------------------------------------------------------------------ */
/*  Attach sub-components                                              */
/* ------------------------------------------------------------------ */

Modal.Header = ModalHeader;
Modal.Content = ModalContent;
Modal.Actions = ModalActions;
